import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, Send } from "lucide-react";
import { toast } from "sonner";
import {
  fetchCorporateOptions,
  submitCorporateInquiry,
} from "@/lib/api";
import { track } from "@/lib/analytics";

const PROGRAMME_LABELS = {
  "on-site": "On-site chair massage",
  packages: "In-clinic workplace packages",
  workshops: "Lunch-and-learn workshops",
};

const PRIMARY_GOALS = [
  "Recovery & injury prevention",
  "Team appreciation / morale",
  "Stress & burnout management",
  "Posture & desk-work health",
  "Recurring employee benefit",
  "One-off corporate event",
];

const EMPTY_FORM = {
  company_name: "",
  industry: "",
  team_size: "",
  contact_name: "",
  contact_role: "",
  contact_email: "",
  contact_phone: "",
  programmes: [],
  preferred_location: "",
  session_frequency: "",
  desired_start: "",
  budget_range: "",
  insurance_provider: "",
  primary_goal: "",
  goals_detail: "",
  additional_notes: "",
  source: "corporate-wellness-page",
};

export default function CorporateInquiryForm() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [options, setOptions] = useState({
    programmes: ["on-site", "packages", "workshops"],
    team_sizes: ["1-10", "11-50", "51-100", "101-250", "250+"],
    frequencies: ["one-time", "monthly", "quarterly", "ad-hoc", "weekly"],
    locations: ["on-site", "in-clinic", "hybrid", "remote-workshop"],
    budgets: [
      "Under $2,000",
      "$2,000 – $5,000",
      "$5,000 – $10,000",
      "$10,000 – $25,000",
      "$25,000+",
      "Open / Needs scoping",
    ],
    industries: [
      "Insurance / Reinsurance",
      "Legal / Professional Services",
      "Fintech / Technology",
      "Hospitality / Aviation",
      "Healthcare",
      "Government / Public Sector",
      "Sports / Athletics",
      "Other",
    ],
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [startedTracked, setStartedTracked] = useState(false);

  useEffect(() => {
    fetchCorporateOptions()
      .then((data) => setOptions((prev) => ({ ...prev, ...data })))
      .catch(() => {});
  }, []);

  const set = (key, value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const onFirstInteraction = () => {
    if (startedTracked) return;
    setStartedTracked(true);
    track("corporate_inquiry_start", { source: "corporate-wellness-page" });
  };

  const toggleProgramme = (key) => {
    onFirstInteraction();
    setForm((prev) => {
      const has = prev.programmes.includes(key);
      return {
        ...prev,
        programmes: has
          ? prev.programmes.filter((k) => k !== key)
          : [...prev.programmes, key],
      };
    });
  };

  const validate = () => {
    const e = {};
    if (!form.company_name.trim()) e.company_name = "Required";
    if (!form.contact_name.trim()) e.contact_name = "Required";
    if (!form.contact_email.trim()) e.contact_email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contact_email))
      e.contact_email = "Looks invalid";
    if (!form.goals_detail.trim() || form.goals_detail.trim().length < 10)
      e.goals_detail = "Please share at least a sentence so we can scope properly.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("A few fields need attention.");
      return;
    }
    setSubmitting(true);
    try {
      await submitCorporateInquiry(form);
      track("corporate_inquiry_submit", {
        team_size: form.team_size,
        programmes_count: form.programmes.length,
      });
      setSubmitted(true);
      toast.success("Brief received — we'll respond within one business day.");
    } catch (err) {
      // FastAPI may return detail as string OR array of {msg}.
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg || JSON.stringify(d)).join(" ")
        : typeof detail === "string"
          ? detail
          : "Couldn't send your brief. Please try again or email us directly.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full px-4 py-3 border border-[#e5e7eb] focus:outline-none focus:border-[#3A7A8C] transition-colors text-[#080808] placeholder:text-[#9CA3AF]";
  const labelCls = "block text-sm font-semibold text-[#080808] mb-2";
  const errCls = "mt-1 text-xs text-[#B91C1C]";

  const summary = useMemo(() => {
    const bits = [];
    if (form.team_size) bits.push(form.team_size + " people");
    if (form.programmes.length)
      bits.push(form.programmes.map((p) => PROGRAMME_LABELS[p] || p).join(", "));
    if (form.session_frequency) bits.push(form.session_frequency);
    return bits.join(" · ");
  }, [form.team_size, form.programmes, form.session_frequency]);

  if (submitted) {
    return (
      <div
        data-testid="corporate-form-success"
        className="border border-[#5FA8B4] bg-[#F5FBFC] p-8 md:p-12"
      >
        <CheckCircle2 className="text-[#3A7A8C]" size={36} />
        <h3 className="mt-5 text-2xl md:text-3xl font-bold text-[#080808] tracking-tight">
          Thank you — your brief is with our team.
        </h3>
        <p className="mt-4 text-[#3C4048] leading-relaxed max-w-xl">
          A senior therapist will review the details and reply within one business day with a
          tailored proposal, sample reports and an insurance eligibility check where relevant.
        </p>
        <button
          type="button"
          data-testid="corporate-form-reset"
          onClick={() => {
            setForm(EMPTY_FORM);
            setSubmitted(false);
            setStartedTracked(false);
          }}
          className="mt-6 ash-link-underline"
        >
          Submit another inquiry
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="corporate-inquiry-form"
      onFocus={onFirstInteraction}
      className="space-y-10"
      noValidate
    >
      {/* Company block */}
      <fieldset className="grid md:grid-cols-2 gap-6">
        <legend className="ash-eyebrow mb-3 col-span-full">Your organisation</legend>
        <div>
          <label htmlFor="company_name" className={labelCls}>Company name *</label>
          <input
            id="company_name"
            type="text"
            value={form.company_name}
            onChange={(e) => set("company_name", e.target.value)}
            data-testid="corp-company-name"
            className={inputCls}
            autoComplete="organization"
          />
          {errors.company_name && <p className={errCls}>{errors.company_name}</p>}
        </div>
        <div>
          <label htmlFor="industry" className={labelCls}>Industry</label>
          <select
            id="industry"
            value={form.industry}
            onChange={(e) => set("industry", e.target.value)}
            data-testid="corp-industry"
            className={inputCls}
          >
            <option value="">Select…</option>
            {options.industries.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="team_size" className={labelCls}>Team size</label>
          <select
            id="team_size"
            value={form.team_size}
            onChange={(e) => set("team_size", e.target.value)}
            data-testid="corp-team-size"
            className={inputCls}
          >
            <option value="">Select…</option>
            {options.team_sizes.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="contact_role" className={labelCls}>Your role</label>
          <input
            id="contact_role"
            type="text"
            value={form.contact_role}
            onChange={(e) => set("contact_role", e.target.value)}
            data-testid="corp-contact-role"
            placeholder="HR Director, Office Manager, COO…"
            className={inputCls}
          />
        </div>
      </fieldset>

      {/* Contact block */}
      <fieldset className="grid md:grid-cols-2 gap-6">
        <legend className="ash-eyebrow mb-3 col-span-full">Primary contact</legend>
        <div>
          <label htmlFor="contact_name" className={labelCls}>Full name *</label>
          <input
            id="contact_name"
            type="text"
            value={form.contact_name}
            onChange={(e) => set("contact_name", e.target.value)}
            data-testid="corp-contact-name"
            className={inputCls}
            autoComplete="name"
          />
          {errors.contact_name && <p className={errCls}>{errors.contact_name}</p>}
        </div>
        <div>
          <label htmlFor="contact_email" className={labelCls}>Work email *</label>
          <input
            id="contact_email"
            type="email"
            value={form.contact_email}
            onChange={(e) => set("contact_email", e.target.value)}
            data-testid="corp-contact-email"
            className={inputCls}
            autoComplete="email"
          />
          {errors.contact_email && <p className={errCls}>{errors.contact_email}</p>}
        </div>
        <div className="md:col-span-2">
          <label htmlFor="contact_phone" className={labelCls}>Phone (optional)</label>
          <input
            id="contact_phone"
            type="tel"
            value={form.contact_phone}
            onChange={(e) => set("contact_phone", e.target.value)}
            data-testid="corp-contact-phone"
            placeholder="+1 441 …"
            className={inputCls}
            autoComplete="tel"
          />
        </div>
      </fieldset>

      {/* Programme block */}
      <fieldset>
        <legend className="ash-eyebrow mb-3">Programme interest</legend>
        <p className="text-sm text-[#3C4048] mb-4">
          Tick any that apply — we&apos;ll suggest the right mix for your team.
        </p>
        <div className="grid sm:grid-cols-3 gap-3" data-testid="corp-programmes">
          {options.programmes.map((key) => {
            const active = form.programmes.includes(key);
            return (
              <button
                type="button"
                key={key}
                onClick={() => toggleProgramme(key)}
                aria-pressed={active}
                data-testid={`corp-programme-${key}`}
                className={`px-4 py-3 text-left text-sm border transition-colors ${
                  active
                    ? "border-[#3A7A8C] bg-[#3A7A8C]/5 text-[#080808]"
                    : "border-[#e5e7eb] hover:border-[#3A7A8C]/40 text-[#3C4048]"
                }`}
              >
                <span className="block font-semibold">
                  {PROGRAMME_LABELS[key] || key}
                </span>
              </button>
            );
          })}
        </div>
      </fieldset>

      {/* Logistics */}
      <fieldset className="grid md:grid-cols-2 gap-6">
        <legend className="ash-eyebrow mb-3 col-span-full">Logistics & timing</legend>
        <div>
          <label htmlFor="preferred_location" className={labelCls}>Preferred location</label>
          <select
            id="preferred_location"
            value={form.preferred_location}
            onChange={(e) => set("preferred_location", e.target.value)}
            data-testid="corp-preferred-location"
            className={inputCls}
          >
            <option value="">Select…</option>
            {options.locations.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="session_frequency" className={labelCls}>Cadence</label>
          <select
            id="session_frequency"
            value={form.session_frequency}
            onChange={(e) => set("session_frequency", e.target.value)}
            data-testid="corp-session-frequency"
            className={inputCls}
          >
            <option value="">Select…</option>
            {options.frequencies.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="desired_start" className={labelCls}>Ideal start</label>
          <input
            id="desired_start"
            type="text"
            value={form.desired_start}
            onChange={(e) => set("desired_start", e.target.value)}
            data-testid="corp-desired-start"
            placeholder="e.g. Q3 2026, next month, 1 May…"
            className={inputCls}
          />
        </div>
        <div>
          <label htmlFor="budget_range" className={labelCls}>Indicative budget</label>
          <select
            id="budget_range"
            value={form.budget_range}
            onChange={(e) => set("budget_range", e.target.value)}
            data-testid="corp-budget-range"
            className={inputCls}
          >
            <option value="">Select…</option>
            {options.budgets.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <label htmlFor="insurance_provider" className={labelCls}>
            Group insurance provider (if applicable)
          </label>
          <input
            id="insurance_provider"
            type="text"
            value={form.insurance_provider}
            onChange={(e) => set("insurance_provider", e.target.value)}
            data-testid="corp-insurance-provider"
            placeholder="CG Insurance, Allshores, Argus, BF&M…"
            className={inputCls}
          />
        </div>
      </fieldset>

      {/* Goals */}
      <fieldset className="grid gap-6">
        <legend className="ash-eyebrow mb-3">Goals</legend>
        <div>
          <label htmlFor="primary_goal" className={labelCls}>Primary objective</label>
          <select
            id="primary_goal"
            value={form.primary_goal}
            onChange={(e) => set("primary_goal", e.target.value)}
            data-testid="corp-primary-goal"
            className={inputCls}
          >
            <option value="">Select…</option>
            {PRIMARY_GOALS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="goals_detail" className={labelCls}>
            Tell us about your team and what success looks like *
          </label>
          <textarea
            id="goals_detail"
            value={form.goals_detail}
            onChange={(e) => set("goals_detail", e.target.value)}
            data-testid="corp-goals-detail"
            rows={5}
            placeholder="Share workplace context, current pain points, prior wellness programmes, etc."
            className={inputCls}
          />
          {errors.goals_detail && <p className={errCls}>{errors.goals_detail}</p>}
        </div>
        <div>
          <label htmlFor="additional_notes" className={labelCls}>Anything else?</label>
          <textarea
            id="additional_notes"
            value={form.additional_notes}
            onChange={(e) => set("additional_notes", e.target.value)}
            data-testid="corp-additional-notes"
            rows={3}
            placeholder="Specific scheduling needs, accessibility, particular therapists, etc."
            className={inputCls}
          />
        </div>
      </fieldset>

      {summary && (
        <p className="text-sm text-[#3C4048]" data-testid="corp-summary">
          <span className="font-semibold text-[#080808]">Summary so far: </span>
          {summary}
        </p>
      )}

      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
        <button
          type="submit"
          disabled={submitting}
          data-testid="corp-submit"
          className="ash-btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {submitting ? "Sending…" : "Send brief"} <Send size={16} />
        </button>
        <p className="text-xs text-[#6B7280] max-w-md">
          We reply within one business day. Your details stay with our clinical team and
          are never used for marketing without permission.
        </p>
      </div>
    </form>
  );
}
